import { Flex, OptionType, SelectMenu, Text } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { dateFormat } from '@fullstory/util/date';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { SnoozeUpdate } from '../../types';
import { WarningBanner } from '../WarningBanner';

const THREE_DAYS_MILLIS = 72 * 60 * 60 * 1000;
const SEVEN_DAYS_MILLIS = 168 * 60 * 60 * 1000;

interface IssueSilenceBase {
  expiresAt: timestamp | null;
}

export type IssuesBannerProps<T> = {
  objectType: string;
  setSnooze: (update: SnoozeUpdate) => void;
  snoozed: T | null;
};

function bannerMessage<T extends IssueSilenceBase>(snoozed: T | null, objectType: string) {
  if (!snoozed) {
    return intl.formatMessage(
      {
        defaultMessage: 'This {objectType} has not generated event traffic in the last 72 hours.',
        id: '9+WSLM',
      },
      { objectType },
    );
  }

  if (snoozed.expiresAt === null) {
    return intl.formatMessage(
      {
        defaultMessage: 'You have snoozed issues from this {objectType} until activity returns.',
        id: 'AbTW6V',
      },
      { objectType },
    );
  }

  return intl.formatMessage(
    {
      defaultMessage: 'You have snoozed issues from this {objectType} until {timestamp}.',
      id: 'dNoe3q',
    },
    {
      objectType,
      timestamp: dateFormat(Timestamp.toDate(snoozed.expiresAt) || new Date(), {
        format: 'abbreviated',
      }),
    },
  );
}

export function IssuesBanner<T extends IssueSilenceBase>({
  objectType,
  setSnooze,
  snoozed,
}: IssuesBannerProps<T>) {
  const snoozeOptions: OptionType<SnoozeUpdate>[] = [
    {
      key: 'unsnooze',
      label: intl.formatMessage({ defaultMessage: 'Snooze', id: '9qc7BX' }),
      value: { type: 'unsnooze' },
    },
    {
      key: '3_days',
      label: intl.formatMessage({ defaultMessage: '3 days', id: 'zFk9rm' }),
      value: { type: 'untilTime', t: Timestamp.fromMillis(Date.now() + THREE_DAYS_MILLIS) },
      testId: `${objectType.toLowerCase()}-definition-details-tab-snooze-filter-3-days`,
    },
    {
      key: '7_days',
      label: intl.formatMessage({ defaultMessage: '7 days', id: 'FrAoo9' }),
      value: { type: 'untilTime', t: Timestamp.fromMillis(Date.now() + SEVEN_DAYS_MILLIS) },
      testId: `${objectType.toLowerCase()}-definition-details-tab-snooze-filter-7-days`,
    },
    {
      key: 'no_expiry',
      label: intl.formatMessage({ defaultMessage: 'Until activity returns', id: 'y1Dm+u' }),
      value: { type: 'untilActivityReturns' },
      testId: `${objectType.toLowerCase()}-definition-details-tab-snooze-filter-until-activity-returns`,
    },
  ];
  const [selected, setSelected] = useState<OptionType<SnoozeUpdate> | undefined>(snoozeOptions[0]);
  return (
    <WarningBanner data-component="WarningBanner">
      <Flex gap={12}>
        <Text appearance="bodyMedium">
          {!snoozed && (
            <FormattedMessage
              defaultMessage="<b>Issue:</b> "
              id="0M59UN"
              values={{
                b: chunks => (
                  <Text appearance="bodyMediumBold" tag="span">
                    {chunks}{' '}
                  </Text>
                ),
              }}
            />
          )}
          {bannerMessage(snoozed, objectType)}
        </Text>
      </Flex>
      <Flex>
        {!snoozed && (
          <SelectMenu
            onChange={opt => {
              if (opt) {
                setSelected(opt);
                setSnooze(opt.value);
              }
            }}
            selected={selected}
            options={snoozeOptions}
            size="x-small"
            data-component="DqmSnoozeMenuOption"
            data-snooze-option={selected?.key}
            data-fs-properties-schema='{"data-snooze-option": "str"}'
            testId={`${objectType.toLowerCase()}-definition-details-tab-snooze-filter`}
          />
        )}
      </Flex>
    </WarningBanner>
  );
}
